import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';

const NotificationContext = createContext(null);

// Default timeouts in ms (0 = requires manual dismissal)
const DEFAULT_TIMEOUTS = {
  error: 0,       // Errors require acknowledgement
  warning: 5000,
  success: 3000,
  info: 4000,
  status: 3000
};

/**
 * NotificationProvider - Global notification system
 *
 * Header alerts (top): errors, warnings, confirmations - things needing attention
 * Footer status (bottom): success, info - transient acknowledgements
 */
export const NotificationProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [status, setStatus] = useState(null);
  const alertIdRef = useRef(0);
  const statusTimeoutRef = useRef(null);

  // Generate unique ID for each alert
  const getNextId = useCallback(() => {
    alertIdRef.current += 1;
    return alertIdRef.current;
  }, []);

  // Add alert to header
  const addAlert = useCallback((type, message, options = {}) => {
    const id = getNextId();
    const timeout = options.timeout !== undefined ? options.timeout : DEFAULT_TIMEOUTS[type];

    const alert = {
      id,
      type,
      message,
      timeout,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      confirmText: options.confirmText || 'Yes',
      cancelText: options.cancelText || 'No'
    };

    setAlerts(prev => [alert, ...prev]);

    // Auto-dismiss if timeout > 0
    if (timeout > 0) {
      setTimeout(() => {
        dismissAlert(id);
      }, timeout);
    }

    return id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNextId]);

  // Dismiss alert by ID
  const dismissAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Show status in footer
  const showStatus = useCallback((message, timeout = DEFAULT_TIMEOUTS.status) => {
    // Clear any existing timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }

    setStatus(message);

    if (timeout > 0) {
      statusTimeoutRef.current = setTimeout(() => {
        setStatus(null);
      }, timeout);
    }
  }, []);

  // Clear status
  const clearStatus = useCallback(() => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setStatus(null);
  }, []);

  // Convenience methods.
  //
  // MEMOISED, and it matters. This was a plain object literal, so `notify` got a fresh identity
  // on every provider render — which makes it a trap in any dependency array. Roughly a dozen
  // components already list it in one; most survive because their callbacks are event handlers
  // rather than effect dependencies. But the moment something does
  //     const load = useCallback(..., [notify]); useEffect(() => load(), [load]);
  // it becomes an infinite render/fetch loop. The report-subscription popup did exactly that and
  // flickered while re-querying the API continuously.
  //
  // Every function below comes from a useCallback with a stable identity, so this memo holds for
  // the life of the provider.
  const notify = useMemo(() => ({
    // Header alerts (top) - things needing attention
    error: (message, options) => addAlert('error', message, options),
    warning: (message, options) => addAlert('warning', message, options),

    // Footer status - success is transient acknowledgement, not an alert
    success: (message, timeout) => showStatus(message, timeout ?? DEFAULT_TIMEOUTS.success),

    // Header success alert (top) - for confirmations the admin must not miss
    // (e.g. test email sent). Use sparingly; footer success is the default.
    successAlert: (message, options) => addAlert('success', message, { timeout: 6000, ...options }),

    // Confirmation with Yes/No buttons (header)
    confirm: (message, onConfirm, options = {}) => {
      return addAlert('confirm', message, {
        ...options,
        timeout: 0, // Confirmations never auto-dismiss
        onConfirm,
        onCancel: options.onCancel || (() => {})
      });
    },

    // Footer status (bottom) - transient acknowledgements
    info: (message, timeout) => showStatus(message, timeout ?? DEFAULT_TIMEOUTS.info),
    status: (message, timeout) => showStatus(message, timeout),

    // Dismissal
    dismiss: dismissAlert,
    clearAlerts,
    clearStatus
  }), [addAlert, showStatus, dismissAlert, clearAlerts, clearStatus]);

  return (
    <NotificationContext.Provider value={{ alerts, status, notify, dismissAlert }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
